import './styles/CopyButton.scss';

import { CheckIcon, CopyIcon, XIcon } from '@phosphor-icons/react';
import { JSX, PropsWithChildren, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tryCatch } from '../../helpers';
import { useAppMessages, useClassHelper } from '../../hooks';
import { Stack } from '../Stack';
import { Tooltip, type ToolTipAlign } from '../Tooltip';

const componentName = 'c-CopyButton';

const VARIANTS = {
  default: 'default',
  topRight: 'top-right',
  rowHeader: 'row-header',
  ghost: 'ghost',
} as const;

interface Props {
  value: string | string[];
  className?: string;
  iconSize?: number;
  label?: string;
  variant?: (typeof VARIANTS)[keyof typeof VARIANTS];
  withToolTip?: boolean;
  disabled?: boolean;
  toolTipAlign?: ToolTipAlign;
  weight?: 'bold' | 'duotone' | 'fill' | 'light' | 'regular' | 'thin';
  feedbackType?: 'toast' | 'inline';
}

export const CopyButton = ({
  value,
  label = '',
  variant = VARIANTS.default,
  withToolTip,
  disabled,
  className,
  iconSize = 24,
  weight = 'regular',
  feedbackType = 'toast',
}: Props) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation();
  const [setMessages] = useAppMessages(t);
  const [currentIcon, setCurrentIcon] = useState<'copy' | 'success' | 'error'>(
    'copy',
  );

  const CurrentIcon = useMemo(() => {
    if (currentIcon === 'success') {
      return CheckIcon;
    }

    if (currentIcon === 'error') {
      return XIcon;
    }

    return CopyIcon;
  }, [currentIcon]);

  const onSuccess = () => {
    if (feedbackType === 'toast') {
      setMessages([
        {
          type: useAppMessages.type.SUCCESS,
          msg: t('testEnvironmentTab_copy_success'),
        },
      ]);
    }

    if (feedbackType === 'inline') {
      setCurrentIcon('success');
      setTimeout(() => {
        setCurrentIcon('copy');
      }, 2000);
    }
  };

  const onError = () => {
    if (feedbackType === 'toast') {
      setMessages([
        {
          type: useAppMessages.type.ERROR,
          msg: t('testEnvironmentTab_copy_error'),
        },
      ]);
    }

    if (feedbackType === 'inline') {
      setCurrentIcon('error');
      setTimeout(() => {
        setCurrentIcon('copy');
      }, 2000);
    }
  };

  const handleCopy = async () => {
    const { error } = await tryCatch(async () => {
      const formattedVal = Array.isArray(value)
        ? value.map(item => item).join('\n')
        : value;

      await window.navigator.clipboard.writeText(formattedVal);
    });

    if (error) {
      console.error(error.message);
      onError();
    }

    onSuccess();
  };

  return (
    <button
      type="button"
      className={c(
        { __button: variant !== VARIANTS.ghost },
        {
          '--with-label': Boolean(label),
        },
        {
          '--top-right': variant === VARIANTS.topRight,
        },
        {
          '--row-header': variant === VARIANTS.rowHeader,
        },
        {
          '--default': variant === VARIANTS.default,
        },
        className,
      )}
      onClick={handleCopy}
      disabled={disabled}
    >
      {label && !withToolTip && <span>{label}</span>}
      <CurrentIcon
        size={iconSize}
        aria-label="copy text icon"
        weight={weight}
      />
    </button>
  );
};

interface CopyButtonWrapperProps
  extends PropsWithChildren,
    Omit<Props, 'value'> {
  value: string | object;
}

const CopyButtonWrapper = ({
  children,
  value,
  label,
  variant = VARIANTS.default,
  withToolTip,
  disabled,
  toolTipAlign = 'right',
  className,
  iconSize,
}: CopyButtonWrapperProps) => {
  const copyValue =
    typeof value === 'object' ? JSON.stringify(value, null, 2) : value;
  const c = useClassHelper(componentName);

  const copyButton = (
    <CopyButton
      value={copyValue}
      variant={variant}
      label={label}
      withToolTip={withToolTip}
      disabled={disabled}
      toolTipAlign={toolTipAlign}
      className={className}
      iconSize={iconSize}
    />
  );

  const wrapWithTooltip = (content: JSX.Element) => {
    return withToolTip ? (
      <Tooltip
        align={toolTipAlign}
        trigger={<div>{content}</div>}
        content={<div>{label}</div>}
      />
    ) : (
      content
    );
  };

  if (variant === VARIANTS.rowHeader) {
    return (
      <Stack direction="column">
        {wrapWithTooltip(copyButton)}
        {children}
      </Stack>
    );
  }

  return (
    <section className={c('__wrapper')}>
      {children}
      {wrapWithTooltip(copyButton)}
    </section>
  );
};

CopyButton.Wrapper = CopyButtonWrapper;
